import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SchoolLogo, ColorBar, Spinner } from '../components/UI';

const MASTER_OTP = '951991';
const DOMAIN     = '@elproschools.edu.in';
const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || '';
const AZURE_TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID || '';
const MICROSOFT_ENABLED = !!(AZURE_CLIENT_ID && AZURE_TENANT_ID);
const MICROSOFT_STATE_KEY = 'sb_microsoft_state';
const MICROSOFT_NONCE_KEY = 'sb_microsoft_nonce';

function randomString(len = 24) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < len; i += 1) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { sendOtp, microsoftLogin, verifyOtp, trustedDeviceLogin, completeRegistration, user } = useAuth();

  const [step, setStep]             = useState('email'); // email | otp | register
  const [email, setEmail]           = useState('');
  const [otp, setOtp]               = useState(['', '', '', '', '', '']);
  const [trustDevice, setTrust]     = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [fullName, setFullName]     = useState('');
  const [campusId, setCampusId]     = useState(1);
  const [campuses, setCampuses]     = useState([
    { campusID: 1, campusName: 'Chinchwad' },
    { campusID: 2, campusName: 'Hinjawadi' },
  ]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [hint, setHint]             = useState('');
  const otpRefs = useRef([]);

  // Redirect if already logged in
  useEffect(() => { if (user) navigate('/dashboard'); }, [user]);

  // Handle Microsoft OAuth redirect callback (#id_token=...)
  useEffect(() => {
    if (!MICROSOFT_ENABLED) return;
    if (!window.location.hash || !window.location.hash.includes('id_token=')) return;

    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const idToken = params.get('id_token');
    const returnedState = params.get('state');
    const oauthError = params.get('error_description') || params.get('error');
    const expectedState = sessionStorage.getItem(MICROSOFT_STATE_KEY);

    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

    if (oauthError) {
      setError(decodeURIComponent(oauthError));
      return;
    }
    if (!idToken) {
      setError('Microsoft login did not return an ID token.');
      return;
    }
    if (expectedState && returnedState !== expectedState) {
      setError('Microsoft login state validation failed.');
      return;
    }

    sessionStorage.removeItem(MICROSOFT_STATE_KEY);
    sessionStorage.removeItem(MICROSOFT_NONCE_KEY);

    (async () => {
      setLoading(true);
      try {
        const res = await microsoftLogin(idToken);
        if (res.success) {
          if (res.data?.isFirstLogin) {
            setFullName(res.data?.user?.fullName || '');
            setStep('register');
          } else {
            navigate('/dashboard');
          }
        } else {
          setError(res.message || 'Microsoft login failed.');
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Microsoft login failed.');
      } finally {
        setLoading(false);
      }
    })();
  }, [microsoftLogin, navigate]);

  // â”€â”€ Send OTP â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleSendOtp(e) {
    e.preventDefault();
    if (!email.toLowerCase().endsWith(DOMAIN)) {
      setError(`Only ${DOMAIN} addresses are allowed.`); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await sendOtp(email);
      if (res.success) {
        setStep('otp');
        setTimeout(() => otpRefs.current[0]?.focus(), 80);
      } else { setError(res.message); }
    } catch (err) {
      if (!err.response) {
        setError('Cannot reach API server. Please start backend and try again.');
      } else {
        setError(err.response?.data?.message || `Failed to send OTP (HTTP ${err.response.status}).`);
      }
    } finally { setLoading(false); }
  }

  async function handleMicrosoftLogin() {
    if (!MICROSOFT_ENABLED) {
      setError('Microsoft sign-in is temporarily unavailable. Please use OTP login and contact support.');
      return;
    }

    const state = randomString(32);
    const nonce = randomString(32);
    sessionStorage.setItem(MICROSOFT_STATE_KEY, state);
    sessionStorage.setItem(MICROSOFT_NONCE_KEY, nonce);

    const redirectUri = `${window.location.origin}/login`;
    const authorizeUrl = `https://login.microsoftonline.com/${AZURE_TENANT_ID}/oauth2/v2.0/authorize`
      + `?client_id=${encodeURIComponent(AZURE_CLIENT_ID)}`
      + `&response_type=id_token`
      + `&redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&response_mode=fragment`
      + `&scope=${encodeURIComponent('openid profile email')}`
      + `&state=${encodeURIComponent(state)}`
      + `&nonce=${encodeURIComponent(nonce)}`
      + `&prompt=select_account`;

    window.location.assign(authorizeUrl);
  }

  // â”€â”€ Trusted device login â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleTrustedLogin() {
    setLoading(true);
    try {
      const res = await trustedDeviceLogin(email || 'trusted@elproschools.edu.in');
      if (res.success) {
        res.data.user.isFirstLogin ? setStep('register') : navigate('/dashboard');
      } else { setError(res.message); }
    } catch { setError('No trusted device found. Please login with OTP.'); }
    finally { setLoading(false); }
  }

  // â”€â”€ OTP input handling â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  function handleOtpChange(i, val) {
    const digit = val.replace(/\D/, '').slice(-1);
    const next  = [...otp];
    next[i]     = digit;
    setOtp(next);
    if (digit && i < 5) setTimeout(() => otpRefs.current[i + 1]?.focus(), 10);
    if (next.every(v => v)) setTimeout(() => submitOtp(next), 150);
  }

  function handleOtpKeyDown(i, e) {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      const next = [...otp]; next[i - 1] = ''; setOtp(next);
      setTimeout(() => otpRefs.current[i - 1]?.focus(), 10);
    }
  }

  async function submitOtp(otpArr) {
    const entered = (otpArr || otp).join('');
    if (entered.length < 6) { otpRefs.current[otp.findIndex(v => !v)]?.focus(); return; }

    // Master OTP bypass
    if (entered === MASTER_OTP) {
      setLoading(true);
      try {
        const res = await verifyOtp(email, entered, trustDevice, deviceName);
        if (res.success) {
          res.data.isFirstLogin ? setStep('register') : navigate('/dashboard');
        } else {
          // Master OTP accepted locally even if API isn't running
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard'); // fallback: master OTP always works offline
      } finally { setLoading(false); }
      return;
    }

    setLoading(true);
    try {
      const res = await verifyOtp(email, entered, trustDevice, deviceName);
      if (res.success) {
        res.data.isFirstLogin ? setStep('register') : navigate('/dashboard');
      } else {
        setOtp(['', '', '', '', '', '']);
        setError(res.message || 'Incorrect OTP. Please try again.');
        setTimeout(() => otpRefs.current[0]?.focus(), 30);
      }
    } catch (err) {
      setOtp(['', '', '', '', '', '']);
      setError(err.response?.data?.message || 'Incorrect OTP. Please try again.');
      setTimeout(() => otpRefs.current[0]?.focus(), 30);
    } finally { setLoading(false); }
  }

  // â”€â”€ Complete Registration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleRegister(e) {
    e.preventDefault();
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    setLoading(true);
    try {
      const res = await completeRegistration(campusId, fullName);
      if (res.success) navigate('/dashboard');
      else setError(res.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  }

  // â”€â”€ Shared wrapper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#EBF4FC 0%,#F4F7FB 50%,#E8F7ED 100%)', padding: '1rem'
    }}>
      <div className="page-anim" style={{
        background: 'white', borderRadius: 20, padding: '2.5rem', width: '100%', maxWidth: 420,
        boxShadow: '0 8px 40px rgba(46,134,200,.18)', border: '1.5px solid var(--border)'
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2rem', gap: 10 }}>
          <SchoolLogo size={64} />
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: 20, fontWeight: 700, textAlign: 'center' }}>Elpro Schools</h1>
          <p style={{ fontSize: 13, color: 'var(--text2)', textAlign: 'center' }}>Booking LE</p>
        </div>
        <ColorBar />

        {error && (
          <div style={{ background: 'var(--red-light)', color: 'var(--red)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            Error: {error}
          </div>
        )}
        {hint && (
          <div style={{ background: 'var(--green-light)', color: 'var(--green)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: '1rem' }}>
            {hint}
          </div>
        )}

        {/* â”€â”€ Step: Email â”€â”€ */}
        {step === 'email' && (
          <form onSubmit={handleSendOtp}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>School Email Address</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder={`yourname${DOMAIN}`}
                style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', outline: 'none', width: '100%' }}
              />
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>Only {DOMAIN} addresses allowed</span>
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px', borderRadius: 8, background: 'var(--blue)', color: 'white', border: 'none',
              fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)'
            }}>
              {loading ? <Spinner size={18} color="white" /> : 'Send OTP'}
            </button>
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={loading}
              style={{
                width: '100%', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px', borderRadius: 8, background: '#fff', color: '#1f3a5f', border: '1.5px solid var(--border)',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)'
              }}
              title={MICROSOFT_ENABLED ? 'Sign in with Microsoft 365' : 'Microsoft sign-in is temporarily unavailable.'}
            >
              {loading ? <Spinner size={16} /> : 'Sign in with Microsoft 365'}
            </button>
            <div style={{ marginTop: '1.2rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>On a trusted device? </span>
              <button type="button" onClick={handleTrustedLogin} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                Login without OTP
              </button>
            </div>

          </form>
        )}

        {/* â”€â”€ Step: OTP â”€â”€ */}
        {step === 'otp' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', textAlign: 'center', marginBottom: '.8rem' }}>
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '1rem 0' }}>
              {[0,1,2,3,4,5].map(i => (
                <input key={i} ref={el => otpRefs.current[i] = el}
                  maxLength={1} inputMode="numeric" value={otp[i]}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  style={{
                    width: 50, height: 56, textAlign: 'center', fontSize: 24, fontWeight: 800,
                    border: `2px solid ${error ? 'var(--red)' : 'var(--border)'}`, borderRadius: 12,
                    outline: 'none', fontFamily: 'var(--font-head)', padding: 0, transition: 'border .15s'
                  }}
                />
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', margin: '8px 0' }}>
              <input type="checkbox" checked={trustDevice} onChange={e => setTrust(e.target.checked)} />
              Trust this device for 30 days
            </label>
            {trustDevice && (
              <input value={deviceName} onChange={e => setDeviceName(e.target.value)}
                placeholder="Device name (e.g. Chrome / Windows)" style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, width: '100%', marginBottom: 8, fontFamily: 'var(--font-body)', outline: 'none' }} />
            )}
            <button onClick={() => submitOtp()} disabled={loading} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px', borderRadius: 8, background: 'var(--blue)', color: 'white', border: 'none',
              fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', marginTop: '.5rem'
            }}>
              {loading ? <Spinner size={18} color="white" /> : 'Verify and Login'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
              <button onClick={() => { setStep('email'); setOtp(['','','','','','']); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Back</button>
              <button onClick={() => { setOtp(['','','','','','']); setError(''); handleSendOtp({ preventDefault: () => {} }); }} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Resend OTP</button>
            </div>
            <div style={{ marginTop: '1rem', padding: 10, background: 'var(--blue-light)', borderRadius: 8, textAlign: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--blue-dark)', fontWeight: 600 }}>Enter your OTP to continue</span>
            </div>
          </div>
        )}

        {/* â”€â”€ Step: Register (first login) â”€â”€ */}
        {step === 'register' && (
          <form onSubmit={handleRegister}>
            <div style={{ padding: '12px', background: 'var(--green-light)', borderRadius: 8, marginBottom: '1.2rem' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>Welcome! Complete your profile</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>This is a one-time setup.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Full Name *</label>
              <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" required
                style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', outline: 'none', width: '100%' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1.4rem' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Campus *</label>
              <select value={campusId} onChange={e => setCampusId(parseInt(e.target.value))}
                style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', outline: 'none', width: '100%', appearance: 'none', background: 'white' }}>
                {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
              </select>
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px', borderRadius: 8, background: 'var(--green)', color: 'white', border: 'none',
              fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)'
            }}>
              {loading ? <Spinner size={18} color="white" /> : 'Complete Setup'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}


